import Button from "components/button";
import Icon from "components/icon";
import Input from "components/input";
import Select from "components/select";
import Typography from "components/typography";
import { Controller } from "react-hook-form";
import DatePicker from "components/date-picker";
import FleetAccordion from "./fleet-accordion";

interface FleetDocumentAccordionProps {
  isOpen: boolean;
  onToggle: () => void;
  onNext: () => void;
  form: any;
  t: any;
  fields: any[];
  onAdd: () => void;
  onRemove: (index: number) => void;
  isUploadingDoc: Record<number, boolean>;
  onUpload: (index: number, file: File) => void;
  expiryWarnings: Record<number, string | null>;
}

const documentTypeOptions = [
  { label: "STNK", value: "STNK" },
  { label: "KIR", value: "KIR" },
  { label: "BPKB", value: "BPKB" },
  { label: "Lainnya", value: "Additional" },
];

interface DocumentUploadFieldProps {
  index: number;
  label: string;
  placeholder: string;
  value: any;
  onChange: (file: File) => void;
  error?: any;
  helperText?: string;
  testId?: string;
  buttonText: string;
  isUploading: boolean;
  originalName?: string;
}

const DocumentUploadField = ({
  index,
  label,
  placeholder,
  value,
  onChange,
  error,
  helperText,
  testId,
  buttonText,
  isUploading,
  originalName,
}: DocumentUploadFieldProps) => (
  <div className="relative">
    <input
      type="file"
      className="hidden"
      id={`file-upload-${index}`}
      accept=".jpg,.jpeg,.png,.pdf"
      onChange={(e) => {
        const file = e.target.files?.[0];
        if (file) onChange(file);
        e.target.value = "";
      }}
    />
    <Input
      label={label}
      placeholder={placeholder}
      readOnly
      className="pr-2"
      value={originalName || (typeof value === "string" ? value.split("/").pop() : value instanceof File ? value.name : "")}
      error={error}
      helperText={helperText}
      rightIcon={
        <Button
          type="button"
          variant="fill"
          color={isUploading ? "secondary" : "primary"}
          size="small"
          className="h-[26px] px-2 text-[10px] leading-none whitespace-nowrap min-w-[70px]"
          onClick={() => document.getElementById(`file-upload-${index}`)?.click()}
          disabled={isUploading}
          data-testid={testId}
        >
          {isUploading ? (
            <div className="flex items-center gap-1">
              <Icon name="spinner" className="animate-spin" size={14} />
              <span>...</span>
            </div>
          ) : (
            buttonText
          )}
        </Button>
      }
    />
  </div>
);

const FleetDocumentAccordion = ({
  isOpen,
  onToggle,
  onNext,
  form,
  t,
  fields,
  onAdd,
  onRemove,
  isUploadingDoc,
  onUpload,
  expiryWarnings,
}: FleetDocumentAccordionProps) => {
  const {
    register,
    control,
    watch,
    formState: { errors },
  } = form;

  return (
    <FleetAccordion
      title={t("accordion-fleet-documents")}
      subtitle={t("accordion-fleet-documents-subtitle")}
      iconName="document-text"
      isOpen={isOpen}
      onToggle={onToggle}
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-4">
          {fields.map((item, index) => (
            <div key={item.id} className="grid grid-cols-[repeat(6,1fr)_32px] gap-4 items-start">
              <div className="col-span-2">
                <Controller
                  name={`documents.${index}.name`}
                  control={control}
                  render={({ field, fieldState }) =>
                    index < 3 ? (
                      <Select
                        label={t("label-document-name")}
                        required
                        options={documentTypeOptions}
                        value={field.value}
                        onValueChange={field.onChange}
                        error={fieldState.error}
                        placeholder={t("placeholder-select")}
                        disabled
                      />
                    ) : (
                      <Input
                        label={t("label-document-name")}
                        value={t("label-document-additional")}
                        readOnly
                        disabled
                      />
                    )
                  }
                />
              </div>

              <div className="col-span-2">
                <Controller
                  name={`documents.${index}.expiryDate`}
                  control={control}
                  render={({ field, fieldState }) => {
                    const docName = watch(`documents.${index}.name`);
                    const hasExpiry = docName === "STNK" || docName === "KIR";
                    
                    if (!hasExpiry) return <div className="h-[62px]" />;

                    const label = docName === "STNK" ? t("label-stnk-expiry") : 
                                docName === "KIR" ? t("label-kir-expiry") : 
                                docName === "BPKB" ? t("label-bpkb-expiry") : 
                                t("label-expired-date");

                    return (
                      <DatePicker
                        label={`${label} *`}
                        value={field.value}
                        onChange={field.onChange}
                        error={fieldState.error}
                        helperText={!fieldState.error && expiryWarnings[index] ? expiryWarnings[index] : undefined}
                        placeholder="dd / mm / yyyy"
                        data-testid={
                          docName === "STNK" ? "datepicker-stnk-expiry" :
                          docName === "KIR" ? "datepicker-kir-expiry" :
                          docName === "BPKB" ? "datepicker-bpkb-expiry" : "datepicker-document-expiry"
                        }
                      />
                    );
                  }}
                />
              </div>

              <div className="col-span-2">
                <Controller
                  name={`documents.${index}.file`}
                  control={control}
                  render={({ field, fieldState }) => {
                    const docName = watch(`documents.${index}.name`);
                    return (
                      <DocumentUploadField
                        index={index}
                        label={`${t("label-upload-document")} ${index < 3 ? "*" : ""}`}
                        placeholder={t("placeholder-upload-document")}
                        value={field.value}
                        originalName={watch(`documents.${index}.originalName`)}
                        onChange={(file) => {
                          field.onChange(file);
                          if (onUpload) onUpload(index, file);
                        }}
                        error={fieldState.error}
                        helperText={t("upload-document-helper")}
                        buttonText={t("btn-upload-file")}
                        isUploading={isUploadingDoc[index]}
                        testId={
                          docName === "STNK" ? "upload-doc-stnk" : 
                          docName === "KIR" ? "upload-doc-kir" : 
                          docName === "BPKB" ? "upload-doc-bpkb" : undefined
                        }
                      />
                    );
                  }}
                />
              </div>

              <div className="pt-8 flex justify-center">
                {index > 2 && (
                  <button
                    type="button"
                    className="text-error-500 hover:text-error-600 transition-colors"
                    onClick={() => onRemove(index)}
                  >
                    <Icon name="minus-cirlce" size={24} />
                  </button>
                )}
              </div>
            </div>
          ))}

          <button
          type="button"
          className="flex items-center gap-2 text-primary-600 hover:text-primary-700 transition-colors font-bold text-sm"
          onClick={onAdd}
        >
          <Icon name="add-circle" size={20} />
          {t("btn-add-document")}
        </button>
        </div>

        <div className="flex justify-end pt-4 border-t border-border mt-4">
          <Button type="button" variant="fill" color="primary" onClick={onNext} className="min-w-[100px]">
            {t("btn-next")}
          </Button>
        </div>
      </div>
    </FleetAccordion>
  );
};

export default FleetDocumentAccordion;
